import { COMPOST_EFFECT_IDS, type CompostEffectId } from "./compostEffects";
import type { CompostBatch, CompostEffect } from "./compost";

/**
 * THE SCRAP COMPOSTER — the rules, as pure functions over a plain state object (reports/composter-scraps-design.html).
 *
 *   1. SCRAPS: every harvested plant drops scraps into the machine's STORAGE (capped by level; a full tank
 *      takes no more). `addScraps`.
 *   2. BATCHES: while switched ON, as soon as storage holds a BATCH worth (12 — far below capacity) and the
 *      finished shelf has room, the machine takes a batch and composts it for a few minutes. Switching OFF
 *      pauses a batch (its progress is kept). `stepComposter` advances all of this by elapsed game time.
 *   3. RANK: a finished compost is ranked 1–5★; the machine's level sets the odds (level 1: 1–2★ … level 5:
 *      5★ common). Each rank carries more effects with higher stats, each rolled within its rank's band.
 *   4. SHELF: finished composts wait on a shelf (size by level) until the player places or discards them.
 *      A batch that finishes onto a FULL shelf waits, complete, until there is room.
 *
 * A compost is the same `CompostBatch` shape pots already carry ({ effects: {type, amount, min, max}[] }), plus a
 * `rank` and an `id`, so the harvest path that pays the effects is unchanged.
 *
 * Levels are 0-based here (tier 0 = "Level 1", the Compost Bin), matching `compostTier` in the save.
 * The tables below are the tuning surface — BALANCE.md mirrors them.
 */

/** Per-level machine stats. */
export interface ComposterLevel {
  /** scraps the storage tank holds */
  storage: number;
  /** finished composts the shelf holds */
  shelf: number;
  /** ms one batch takes to compost */
  processMs: number;
  /** odds of each rank ★1..★5 (sums to 1) */
  odds: [number, number, number, number, number];
}
export const COMPOSTER_LEVELS: ComposterLevel[] = [
  { storage: 30, shelf: 3, processMs: 240_000, odds: [0.75, 0.25, 0, 0, 0] },
  { storage: 45, shelf: 4, processMs: 210_000, odds: [0.55, 0.33, 0.12, 0, 0] },
  { storage: 65, shelf: 6, processMs: 180_000, odds: [0.35, 0.35, 0.22, 0.08, 0] },
  { storage: 90, shelf: 8, processMs: 150_000, odds: [0.2, 0.3, 0.28, 0.16, 0.06] },
  { storage: 120, shelf: 10, processMs: 120_000, odds: [0.08, 0.2, 0.3, 0.26, 0.16] },
];
export function composterLevel(tier: number): ComposterLevel {
  return COMPOSTER_LEVELS[Math.max(0, Math.min(COMPOSTER_LEVELS.length - 1, Math.round(tier)))]!;
}
/** Scraps one batch consumes — also the start threshold. Constant across levels on purpose: the tank's
 *  "next batch" line sits at the same place at every level. */
export const BATCH_SCRAPS = 12;
/** The best rank a level can produce (its highest non-zero odds). */
export function maxRank(tier: number): number {
  const o = composterLevel(tier).odds;
  for (let r = 5; r >= 1; r--) if (o[r - 1]! > 0) return r;
  return 1;
}

/** Each rank's base stat band (before the effect weight) and how many effects it carries. */
export const RANK_BANDS: Array<[number, number]> = [[5, 10], [10, 16], [16, 23], [23, 30], [30, 40]];
/** Effect weights: a strong effect (Double Harvest) can't hit the same raw number as a gentle one. Growth Speed
 *  tops out at 40% so the whole speed stack stays inside MAX_SPEED_MULT (server-clamped); Moisture Lock stays
 *  under its own 80% cap. */
export const EFFECT_WEIGHT: Record<CompostEffectId, number> = {
  quickBloom: 1.0, retain: 1.5, double: 0.6, value: 1.0, xp: 1.0, pollinate: 0.8,
};
/** Effects a compost of this rank carries. ★★★★★ carries 3–5 (50 / 35 / 15 %), so a five-effect bag is the
 *  rare prize inside the prize. */
function effectCount(rank: number, rng: () => number): number {
  if (rank >= 5) { const r = rng(); return r < 0.5 ? 3 : r < 0.85 ? 4 : 5; }
  if (rank === 4) return rng() < 0.25 ? 3 : 2;
  if (rank === 3) return 2;
  return 1;
}

/** BAD-LUCK PROTECTION (levels 4–5): this many batches in a row below ★3 guarantees the next is ≥ ★3; at
 *  level 5, this many in a row without a ★5 guarantees one. Invisible most of the time. */
export const PITY_THREE_AFTER = 15;
export const PITY_FIVE_AFTER = 40;

/** A finished compost: the pot batch shape plus its rank and a stable id (for the shelf / drag / keys). */
export interface Compost extends CompostBatch { id: string; rank: number; }

/** A batch in the machine. `progressMs` accumulates only while the machine is ON (so OFF pauses it). */
export interface ComposterJob { tier: number; seed: number; progressMs: number; }

export interface ComposterState {
  on: boolean;
  scraps: number;
  job: ComposterJob | null;
  finished: Compost[];
  /** composts made in total (seeds the next batch's roll; an achievement metric later) */
  made: number;
  /** the best rank ever made (an achievement metric — the shelf alone forgets once a compost is placed) */
  best: number;
  /** bad-luck counters: batches since a ≥★3, and since a ★5 */
  sinceThree: number;
  sinceFive: number;
  /** game time the state was last advanced to */
  lastAt: number;
}

export function freshComposter(now: number): ComposterState {
  return { on: true, scraps: 0, job: null, finished: [], made: 0, best: 0, sinceThree: 0, sinceFive: 0, lastAt: now };
}

// ---- rolling ---------------------------------------------------------------
function seededRng(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0 || 1;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 0x100000000; };
}

/** Roll a rank for a level, with bad-luck protection. */
export function rollRank(tier: number, rng: () => number, sinceThree = 0, sinceFive = 0): number {
  const lv = Math.max(0, Math.min(4, Math.round(tier)));
  const odds = composterLevel(lv).odds;
  let r = rng(), rank = 1;
  for (let i = 0; i < 5; i++) { if (r < odds[i]!) { rank = i + 1; break; } r -= odds[i]!; if (i === 4) rank = maxRank(lv); }
  if (lv >= 3 && sinceThree >= PITY_THREE_AFTER - 1) rank = Math.max(rank, 3);
  if (lv >= 4 && sinceFive >= PITY_FIVE_AFTER - 1) rank = 5;
  return rank;
}

/** Roll a rank's effects: `effectCount` distinct effect types, each stat uniform in its weighted band. */
export function rollEffects(rank: number, rng: () => number): CompostEffect[] {
  const [lo, hi] = RANK_BANDS[Math.max(1, Math.min(5, rank)) - 1]!;
  const pool = [...COMPOST_EFFECT_IDS];
  const out: CompostEffect[] = [];
  const n = effectCount(rank, rng);
  for (let i = 0; i < n && pool.length; i++) {
    const type = pool.splice(Math.floor(rng() * pool.length), 1)[0]!;
    const w = EFFECT_WEIGHT[type];
    const min = Math.round(lo * w), max = Math.round(hi * w);
    out.push({ type, amount: Math.round(min + rng() * (max - min)), min, max });
  }
  // passives (growth, moisture) read last, after the harvest chances — same order the old batches used
  const passive = (t: CompostEffectId) => (t === "quickBloom" || t === "retain" ? 1 : 0);
  return out.sort((a, b) => passive(a.type) - passive(b.type));
}

/** One compost rolled outright at a level (no bad-luck counters) — for resolving an old sealed barrel. */
export function rollCompost(tier: number, seed: number): Compost {
  const rng = seededRng(seed);
  const rank = rollRank(tier, rng);
  return { id: `c${(seed >>> 0).toString(36)}`, rank, effects: rollEffects(rank, rng) };
}

function finishJob(s: ComposterState, job: ComposterJob): ComposterState {
  const rng = seededRng(job.seed);
  const rank = rollRank(job.tier, rng, s.sinceThree, s.sinceFive);
  const compost: Compost = { id: `c${job.seed.toString(36)}`, rank, effects: rollEffects(rank, rng) };
  return {
    ...s, job: null, finished: [...s.finished, compost], made: s.made + 1, best: Math.max(s.best ?? 0, rank),
    sinceThree: rank >= 3 ? 0 : s.sinceThree + 1,
    sinceFive: rank >= 5 ? 0 : s.sinceFive + 1,
  };
}

// ---- the state machine -----------------------------------------------------
/** Add harvested scraps, clamped to the tank. Returns the new state and how many actually went in. */
export function addScraps(s: ComposterState, n: number, tier: number): { state: ComposterState; added: number } {
  const room = Math.max(0, composterLevel(tier).storage - s.scraps);
  const added = Math.max(0, Math.min(room, Math.floor(n)));
  return { state: added ? { ...s, scraps: s.scraps + added } : s, added };
}

/** Composts that finished during one step (for toasts / reveals). */
export interface StepResult { state: ComposterState; completed: Compost[]; }

/**
 * Advance the machine to `now`. Chains as many batches as time, scraps and shelf room allow. Elapsed time is
 * capped at `maxStepMs` (the away cap), so a long absence credits at most that much composting. A new batch
 * is stamped with the CURRENT level (an upgrade mid-batch doesn't change a batch already running).
 */
export function stepComposter(s0: ComposterState, now: number, tier: number, maxStepMs: number): StepResult {
  let s = s0;
  const completed: Compost[] = [];
  let dt = Math.max(0, Math.min(maxStepMs, now - s.lastAt));
  const shelfCap = composterLevel(tier).shelf;
  for (let guard = 0; guard < 500; guard++) {
    // start a batch if idle, ON, stocked and the shelf has room
    if (!s.job) {
      if (!s.on || s.scraps < BATCH_SCRAPS || s.finished.length >= shelfCap) break;
      const seed = ((s.made + 1) * 2654435761 ^ Math.floor(s.lastAt + (now - s.lastAt) - dt)) >>> 0;
      s = { ...s, scraps: s.scraps - BATCH_SCRAPS, job: { tier: Math.round(tier), seed, progressMs: 0 } };
    }
    const job = s.job!;
    if (!s.on) break;   // OFF: the batch holds its progress
    const need = composterLevel(job.tier).processMs - job.progressMs;
    if (need > dt) { s = { ...s, job: { ...job, progressMs: job.progressMs + dt } }; dt = 0; break; }
    // the batch completes within this step
    dt -= Math.max(0, need);
    const done = { ...job, progressMs: composterLevel(job.tier).processMs };
    if (s.finished.length >= shelfCap) { s = { ...s, job: done }; break; }   // waits, complete, for shelf room
    const before = s.finished.length;
    s = finishJob(s, done);
    completed.push(s.finished[before]!);
  }
  return { state: { ...s, lastAt: now }, completed };
}

/** 0..1 progress of the current batch (−1 when idle), extrapolated to `now` while ON. */
export function jobProgress(s: ComposterState, now: number): number {
  if (!s.job) return -1;
  const total = composterLevel(s.job.tier).processMs;
  const extra = s.on ? Math.max(0, now - s.lastAt) : 0;
  return Math.max(0, Math.min(1, (s.job.progressMs + extra) / total));
}
/** ms left on the current batch (0 when idle or complete). */
export function jobRemainingMs(s: ComposterState, now: number): number {
  if (!s.job) return 0;
  const total = composterLevel(s.job.tier).processMs;
  return Math.max(0, total * (1 - jobProgress(s, now)));
}

// ---- ranks for older composts ----------------------------------------------
/** The rank a compost reads as: its own, or — for a batch made by the old composter — derived from its
 *  strongest effect (amount ÷ that effect's weight, placed in the rank bands). */
export function rankOf(b: CompostBatch & { rank?: number }): number {
  if (typeof b.rank === "number" && b.rank >= 1) return Math.min(5, Math.round(b.rank));
  let best = 0;
  for (const e of b.effects) best = Math.max(best, e.amount / (EFFECT_WEIGHT[e.type] ?? 1));
  for (let r = 5; r >= 2; r--) if (best >= RANK_BANDS[r - 1]![0]) return r;
  return 1;
}

/** "★★★" for a rank. */
export function starString(rank: number): string { return "★".repeat(Math.max(1, Math.min(5, Math.round(rank)))); }

// ---- persistence ---------------------------------------------------------------------------------------------
/** Validate a saved composter (idempotent, like every other save field). Unknown shape → null. */
export function coerceComposter(raw: unknown, now: number, coerceBatch: (v: unknown) => CompostBatch | null): ComposterState | null {
  const o = raw as Record<string, unknown> | null | undefined;
  if (!o || typeof o !== "object" || Array.isArray(o)) return null;
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  const finished: Compost[] = [];
  if (Array.isArray(o.finished)) {
    for (const f of o.finished) {
      const b = coerceBatch(f); if (!b) continue;
      const ff = f as Record<string, unknown>;
      finished.push({ ...b, id: typeof ff.id === "string" ? ff.id : `m${finished.length}_${Math.floor(now).toString(36)}`, rank: rankOf({ ...b, rank: num(ff.rank, 0) }) });
    }
  }
  let job: ComposterJob | null = null;
  const j = o.job as Record<string, unknown> | null | undefined;
  if (j && typeof j === "object" && !Array.isArray(j)) {
    job = { tier: Math.max(0, Math.min(4, Math.round(num(j.tier, 0)))), seed: Math.floor(num(j.seed, 1)) >>> 0, progressMs: Math.max(0, num(j.progressMs, 0)) };
  }
  return {
    on: o.on !== false,
    scraps: Math.max(0, Math.floor(num(o.scraps, 0))),
    job, finished,
    made: Math.max(0, Math.floor(num(o.made, 0))),
    best: Math.max(Math.min(5, Math.floor(num(o.best, 0))), ...finished.map((c) => c.rank), 0),
    sinceThree: Math.max(0, Math.floor(num(o.sinceThree, 0))),
    sinceFive: Math.max(0, Math.floor(num(o.sinceFive, 0))),
    lastAt: num(o.lastAt, now),
  };
}

/**
 * MIGRATE the old composter into the new one, once: every batch in the old BIN becomes a finished compost (ranked
 * from its strongest effect); a SEALED old barrel resolves into one more. Plants in an UNSEALED barrel go back to
 * stock (returned via `returnPlants`). Idempotent: the caller writes the old bin/barrel back empty afterwards.
 */
export function migrateOldComposter(
  state: ComposterState, oldBin: CompostBatch[], oldBarrel: { plants: string[]; sealedAt?: number } | null | undefined,
  resolveSealed: () => CompostBatch | null, returnPlants: (ids: string[]) => void,
): ComposterState {
  let s = state;
  const add: Compost[] = oldBin.map((b, i) => ({ ...b, id: `old${i}_${s.made}`, rank: rankOf(b) }));
  if (oldBarrel?.sealedAt) {
    const b = resolveSealed();
    if (b) add.push({ ...b, id: `oldbarrel_${s.made}`, rank: rankOf(b) });
  } else if (oldBarrel?.plants?.length) {
    returnPlants(oldBarrel.plants);
  }
  if (add.length) s = { ...s, finished: [...s.finished, ...add], best: Math.max(s.best ?? 0, ...add.map((c) => c.rank)) };
  return s;
}

// ---- what the machine in the greenhouse shows ---------------------------------------------------------------
/** The composter's state as its ART needs it, per frame (RackLayer.drawEquipment). */
export interface ComposterVisual {
  /** storage fill 0..1 */
  fill: number;
  /** a batch is being mulched right now (ON, not yet complete) */
  running: boolean;
  on: boolean;
  /** 0..1 progress of the batch in the machine; −1 with none */
  progress: number;
  /** ranks of the finished composts waiting on the shelf, best first, at most 3 (the sacks at its foot) */
  waiting: number[];
  shelfFull: boolean;
  tankFull: boolean;
}
export const IDLE_COMPOSTER_VISUAL: ComposterVisual = { fill: 0, running: false, on: true, progress: -1, waiting: [], shelfFull: false, tankFull: false };
export function composterVisual(s: ComposterState, tier: number, now: number): ComposterVisual {
  const lv = composterLevel(tier);
  const prog = s.job ? jobProgress(s, now) : -1;
  return {
    fill: Math.max(0, Math.min(1, s.scraps / lv.storage)),
    running: !!s.job && s.on && prog < 1,
    on: s.on,
    progress: prog,
    waiting: s.finished.map((c) => c.rank).sort((a, b) => b - a).slice(0, 3),
    shelfFull: s.finished.length >= lv.shelf,
    tankFull: s.scraps >= lv.storage,
  };
}
